import { useState } from "react";
import { Pager } from "@/app/Pager";
import { useCanWrite } from "@/app/auth";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { prefixes, type Input, type NumberingPlan } from "@/app/master";
import { loadingOf, useList } from "@/app/queries";
import { usePage } from "@/app/usePage";
import { Alert, Button, EmptyState, formatTime, Input as TextInput, PageHeader, Table, TableWrap, Th, Toolbar } from "@/ui";
import { PrefixForm } from "./PrefixForm";
import { PrefixImport } from "./PrefixImport";
import { prefixDraft, prefixInput } from "./prefixDraft";

/** 号码前缀：按前缀或运营商搜索，新建与编辑在抽屉里，批量导入走 CSV（#138）。 */
export function Numbering() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: NumberingPlan | null } | null>(null);
  const [importing, setImporting] = useState(false);
  const list = useList("prefixes", prefixes);
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? []).filter((p) => !keyword || p.prefix.startsWith(keyword) || p.operator_name.toLowerCase().includes(keyword) || p.mcc + "-" + p.mnc === keyword);
  const paged = usePage("prefixes", shown, keyword);

  return (
    <>
      <PageHeader title="号码前缀" description="号码前缀与运营商的对应，按最长前缀匹配；停用的前缀不参与匹配，回退到更短的前缀。保存即生效。"
        actions={canWrite && <><Button onClick={() => setImporting(true)}>批量导入</Button><Button variant="primary" onClick={() => setEditing({ row: null })}>新建前缀</Button></>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar>
        <TextInput size="sm" className="ax-search" placeholder="搜索前缀、运营商或 MCC-MNC" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <>
            <Table dense>
              <thead><tr><Th w={136}>前缀</Th><Th w={72}>国家</Th><Th w="code">MCC</Th><Th w="code">MNC</Th><Th w="name">运营商</Th><Th w="status">状态</Th><Th numeric w={160}>更新时间</Th><Th /></tr></thead>
              <tbody>
                {paged.rows.map((p) => (
                  <tr key={p.prefix}>
                    <td className="ax-mono">{p.prefix}</td><td className="ax-mono">{p.country}</td><td className="ax-mono">{p.mcc}</td><td className="ax-mono">{p.mnc}</td><td>{p.operator_name}</td>
                    <td><EntityStatusPill status={p.status} /></td><td className="ax-num ax-mono">{formatTime(p.updated_at, false)}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => setEditing({ row: p })}>{canWrite ? "编辑" : "查看"}</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !list.isPending && <EmptyState title="没有前缀">号码归属由接入层按最长前缀解析（决策 D16）。</EmptyState>
        )}
      </TableWrap>
      {importing && <PrefixImport onClose={() => setImporting(false)} />}
      {editing && (
        <EntityDrawer title={editing.row ? `编辑前缀 ${editing.row.prefix}` : "新建前缀"} initial={prefixDraft(editing.row ?? undefined)} convert={prefixInput}
          save={(body: Input<NumberingPlan>) => (editing.row ? prefixes.update(editing.row.prefix, body) : prefixes.create(body))}
          invalidate={["prefixes"]} message={editing.row ? "已更新前缀" : "已新建前缀"} deletion={editing.row ? deleteTarget("前缀", editing.row.prefix, prefixes, editing.row.prefix, ["prefixes"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <PrefixForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} />}
        </EntityDrawer>
      )}
    </>
  );
}
